import React, { useEffect, useRef, useState, type RefObject } from "react";
import { createPortal } from "react-dom";
import {
  getVariantLabel,
  isBundleProductPage,
  isDeviceSubscriptionProduct,
} from "@/types/product";
import { useProduct } from "@/hooks/useProduct";
import { useCart } from "@/hooks/useCart";
import { useIsB2b } from "@/hooks/useIsB2b";
import { DiscountedUnitPrice } from "@/components/common/DiscountedUnitPrice/DiscountedUnitPrice";
import { isProductDiscounted } from "@/utils/discount";
import { useLocalizedTexts, t } from "@/utils/translations";
import { trackAddToCart } from "@/components/analytics/gtm-events";
import { useAnalyticsIdentity } from "@/components/analytics/AnalyticsIdentityContext";
import { CartPlusIcon } from "@/components/icons";
import { fixedCmsImage } from "@/utils/cmsImage";
import styles from "./StickyCartBar.module.css";

interface Props {
  mainButtonRef: RefObject<HTMLButtonElement>;
  quantity: number;
}

export const StickyCartBar: React.FC<Props> = ({ mainButtonRef, quantity }) => {
  const { productData, requiresVariantSelection, selectedVariant } =
    useProduct();
  const { addToCart, isAddingToCart, refreshCart, setDrawerOpen } = useCart();
  const isB2b = useIsB2b();
  const identity = useAnalyticsIdentity();
  const localizedTexts = useLocalizedTexts();
  const [visible, setVisible] = useState(false);
  const barRef = useRef<HTMLDivElement>(null);

  const isDeviceSubscription = isDeviceSubscriptionProduct(productData);
  // A bundle cannot be quick-added: it goes to a different endpoint, and its
  // per-item version choices live in the picker further up the page, which this
  // bar has no way to collect. Quick-adding it would post the bundle to the
  // ordinary add-to-cart, which the backend rejects.
  const isBundle = isBundleProductPage(productData);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (isDeviceSubscription || isBundle) return;
    if (typeof IntersectionObserver === "undefined") return;
    const target = mainButtonRef.current;
    if (!target) return;
    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry) setVisible(!entry.isIntersecting);
      },
      { threshold: 0 }
    );
    io.observe(target);
    return () => io.disconnect();
  }, [mainButtonRef, isDeviceSubscription, isBundle]);

  if (isDeviceSubscription || isBundle) return null;
  if (!mounted) return null;

  const productName = productData.name ?? "";
  const priceInfo = productData.compositions?.product?.price;
  const price = priceInfo?.formattedCalculatedPrice ?? "";
  const productReference = productData.compositions?.product?.productReference;
  const imageUrl = productData.image?.url;

  // Mirrors the hero button: a variant product can only be added once a version
  // is picked, and the picker lives further up the page.
  const isBlockedOnVariant = requiresVariantSelection && !selectedVariant;

  const handleAdd = async () => {
    if (isAddingToCart || !productReference || isBlockedOnVariant) return;
    await addToCart(productReference, quantity, selectedVariant?.id);
    await refreshCart();
    setDrawerOpen(true);
    const calculatedPrice =
      productData.compositions?.product?.price?.calculatedPrice ?? 0;
    trackAddToCart({
      product: productData,
      currency: productData.compositions?.product?.price?.currency ?? "",
      price: calculatedPrice,
      value: calculatedPrice * quantity,
      quantity,
      variantSku: selectedVariant?.sku ?? undefined,
      identity,
    });
  };

  // While a version is still unpicked the bar sends the customer back up to the
  // picker rather than presenting a dead disabled button — the picker is off
  // screen precisely when this bar is visible.
  const handleCtaClick = () => {
    if (isBlockedOnVariant) {
      const target = mainButtonRef.current;
      if (typeof target?.scrollIntoView === "function") {
        target.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }
    void handleAdd();
  };

  const addLabel = t(
    localizedTexts,
    "Tracker.Products.AddToCart",
    "Add to cart"
  );
  const accessibleLabel = productName
    ? `${addLabel} — ${productName}`
    : addLabel;

  const bar = (
    <div
      ref={barRef}
      className={styles.bar}
      data-visible={visible ? "true" : undefined}
      aria-hidden={!visible}
      role="region"
      aria-label={t(
        localizedTexts,
        "Tracker.Products.StickyAddToCartLabel",
        "Quick add to cart"
      )}
    >
      <div className={styles.miniImg}>
        {imageUrl && (
          <img
            // At most 80% of the 48px box (38.4px).
            {...fixedCmsImage(imageUrl, { width: 40 })}
            alt=""
            loading="lazy"
          />
        )}
      </div>
      <div className={styles.info}>
        {productName && <div className={styles.name}>{productName}</div>}
        {selectedVariant && (
          <div className={styles.variant}>
            {getVariantLabel(selectedVariant)}
          </div>
        )}
        {isB2b &&
        priceInfo &&
        (priceInfo.formattedListPrice ||
          priceInfo.formattedCalculatedPriceWithoutVat) ? (
          <DiscountedUnitPrice
            variant="b2b"
            standardInclVat={priceInfo.formattedListPrice}
            accentPrice={priceInfo.formattedCalculatedPriceWithoutVat}
            size="sm"
            onDark
          />
        ) : !isB2b &&
          isProductDiscounted(priceInfo) &&
          priceInfo?.formattedListPrice &&
          priceInfo?.formattedCalculatedPrice ? (
          <DiscountedUnitPrice
            variant="discount"
            standardInclVat={priceInfo.formattedListPrice}
            accentPrice={priceInfo.formattedCalculatedPrice}
            size="sm"
            onDark
          />
        ) : (
          price && <div className={styles.price}>{price}</div>
        )}
      </div>
      <button
        type="button"
        className={styles.cta}
        onClick={handleCtaClick}
        disabled={isAddingToCart}
        tabIndex={visible ? 0 : -1}
        aria-label={accessibleLabel}
      >
        <CartPlusIcon />
        <span className={styles.ctaLabel}>{addLabel}</span>
      </button>
    </div>
  );

  return createPortal(bar, document.body);
};

export default StickyCartBar;
